// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { BilalMark, type MarkState } from './BilalMark.js';
import { stageLine } from '../stage-lines.js';
import { messages, useLanguage, useMessages } from '../i18n.js';
import type { VoiceLoopState } from '../voice/voice-loop.js';

/**
 * The live voice screen: one large presence that is genuinely doing what it shows — listening with
 * the microphone's real level, thinking through the server's truthful stages, speaking with the
 * playback's real amplitude. Words appear as they are heard; notices are shown, never spoken; one
 * control ends it. Everything here renders a state the loop controller owns.
 */
type Codes = Readonly<Record<string, string>>;

const STATUS_AR: Codes = {
  connecting: 'جارٍ التحضير…',
  listening: 'أسمعك…',
  speaking: 'أتحدث…',
};
const STATUS_EN: Codes = {
  connecting: 'Getting ready…',
  listening: 'Listening…',
  speaking: 'Speaking…',
};
const ENDINGS_AR: Codes = {
  unavailable: 'الوضع الصوتي غير متاح الآن. اكتب رسالتك، أو جرّب لاحقًا.',
  failed: 'انقطع الالتقاط. جرّب الدخول للوضع الصوتي من جديد.',
  session_closed: 'انتهت هذه الجلسة.',
};
const ENDINGS_EN: Codes = {
  unavailable: 'Voice mode is unavailable right now. Type your message, or try later.',
  failed: 'The capture stopped. Try entering voice mode again.',
  session_closed: 'This conversation has ended.',
};

const T = messages({
  ar: {
    region: 'محادثة صوتية',
    exit: 'إنهاء المحادثة الصوتية',
    hint: 'تحدّث بحرّية — وعندما تسكت، يرد بلال. اضغط ✕ للخروج.',
    status: STATUS_AR,
    endings: ENDINGS_AR,
  },
  en: {
    region: 'Voice conversation',
    exit: 'End the voice conversation',
    hint: 'Speak freely — when you stop, Bilal answers. Press ✕ to leave.',
    status: STATUS_EN,
    endings: ENDINGS_EN,
  },
});

function markStateFor(state: VoiceLoopState): MarkState {
  if (state.kind === 'listening') return 'listening';
  if (state.kind === 'thinking') return 'thinking';
  if (state.kind === 'speaking') return 'speaking';
  if (state.kind === 'ended') return 'completed';
  return 'idle';
}

export function VoiceMode({
  state,
  depth,
  onExit,
}: {
  readonly state: VoiceLoopState;
  readonly depth: 'quick' | 'balanced' | 'deep';
  readonly onExit: () => void;
}): React.JSX.Element {
  const t = useMessages(T);
  const language = useLanguage();
  const amplitude =
    state.kind === 'listening' || state.kind === 'speaking' || state.kind === 'reciting'
      ? state.level
      : 0;
  return (
    <div className="voice-overlay" role="dialog" aria-label={t.region}>
      <button type="button" className="voice-exit" onClick={onExit} aria-label={t.exit}>
        ✕
      </button>
      <div className="voice-stage">
        <BilalMark state={markStateFor(state)} depth={depth} size={132} amplitude={amplitude} />
        <p role="status" className="voice-status-line">
          {state.kind === 'thinking'
            ? stageLine(state.stage, language)
            : (t.status[state.kind] ?? '')}
        </p>
        {state.kind === 'listening' && (state.committed.length > 0 || state.interim.length > 0) && (
          <p className="voice-words" dir="auto">
            {state.committed.length > 0 && (
              <span className="live-committed">{state.committed}</span>
            )}
            {state.interim.length > 0 && <span className="live-interim">{state.interim}</span>}
          </p>
        )}
        {state.kind === 'notice' && <p className="voice-notice">{state.text}</p>}
        {state.kind === 'ended' && state.reason !== 'exited' && (
          <p className="voice-notice">{t.endings[state.reason] ?? ''}</p>
        )}
      </div>
      <p className="voice-hint">{t.hint}</p>
    </div>
  );
}
